import React from 'react';
import { Container, Typography, Button } from '@material-ui/core';
import { isChunkLoadError, reloadOnceForStaleChunk } from '../../../Core/Basic/chunk-reload';

// Error boundary for the routed view (the part that renders React.lazy pages).
//
// Guarantees a failed code-split chunk NEVER leaves a blank screen:
//  - A stale-chunk failure (old tab, new deploy) triggers a one-time reload to
//    fetch the fresh build, showing an "updating" message meanwhile.
//  - Any other render-time error shows a generic fallback with a manual reload
//    button instead of unmounting the whole app.
class RouteErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isChunkError: false };
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, isChunkError: isChunkLoadError(error) };
    }

    componentDidCatch(error, errorInfo) {
        if (isChunkLoadError(error)) {
            // Stale deployment: reload once (rate-limited) to get the new build.
            reloadOnceForStaleChunk();
            return;
        }
        // Genuine render error: log for diagnostics, keep the fallback visible.
        console.error('[RouteErrorBoundary] Uncaught error in routed view:', error, errorInfo);
    }

    handleReload() {
        window.location.reload();
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        if (this.state.isChunkError) {
            return (
                <Container maxWidth="sm" style={{ textAlign: 'center', paddingTop: 80 }}>
                    <Typography variant="h5" gutterBottom>
                        A new version is available. Updating…
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                        Please wait while the latest version loads.
                    </Typography>
                </Container>
            );
        }

        return (
            <Container maxWidth="sm" style={{ textAlign: 'center', paddingTop: 80 }}>
                <Typography variant="h5" color="error" gutterBottom>
                    Something went wrong.
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 24 }}>
                    An unexpected error occurred while loading this page.
                </Typography>
                <Button variant="contained" color="primary" onClick={this.handleReload}>
                    Reload Page
                </Button>
            </Container>
        );
    }
}

export default RouteErrorBoundary;
